// StepAddAssets.jsx
import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import * as XLSX from "xlsx";

// ** MUI Components
import {
  Box, Button, Grid, Typography, IconButton, Avatar,
  List, Tooltip, Tab, CircularProgress, Backdrop
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTheme, styled } from "@mui/material/styles";
import { TabContext, TabPanel } from "@mui/lab";
import MuiTabList from "@mui/lab/TabList";

import AddAssetDialog from "screens/Inventory/component/AddAsset";
import AddAssetTypeDialog from "screens/Inventory/component/AddAssetType";
import { useIndexLogic } from "screens/Inventory/logic/index-logic";

// ** Custom Imports
import Icon from "components/icon";
import AddIcon from "@mui/icons-material/Add";
import { useDropzone } from "react-dropzone";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from 'react-toastify';
// ** Assets
import AddEmployeeDoodle from "images/AddEmployeeDoodle.png";
import XLSX_Placeholder from "images/Attachment_Icons/XLSX__PlaceholderIcon.png";
import CSV_Placeholder from "images/Attachment_Icons/CSV__PlaceholderIcon.png";

const TabList = styled(MuiTabList)(({ theme }) => ({
  borderBottom: "0 !important",
  width: '90%',
  display: 'flex',
  justifyContent: 'center',
  "&, & .MuiTabs-scroller": {
    boxSizing: "content-box",
    padding: theme.spacing(1.25, 1.25, 2),
    margin: `${theme.spacing(-1.25, -1.25, -2)} !important`,
    backgroundColor: '#F3F4F4',
    maxWidth: 'max-content',
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    justifyContent: 'center',
    // Responsive padding for mobile
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1, 1, 1.5),
      margin: `${theme.spacing(-1, -1, -1.5)} !important`,
    }
  },
  "& .MuiTabs-flexContainer": {
    justifyContent: 'center',
    // Reduce gap between tabs on mobile
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.5),
    }
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "& .Mui-selected": {
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.common.white,
    color: `${theme.palette.main.main} !important`,
  },
  "& .MuiTab-root": {
    display: 'flex',
    justifyContent: 'center',
    lineHeight: 1,
    borderRadius: theme.shape.borderRadius,
    // Smaller padding and min-height for mobile
    [theme.breakpoints.down('sm')]: {
      minHeight: '36px',
      padding: theme.spacing(0.75, 1.5),
      fontSize: '0.8rem',
    },
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
}));


const StepAddAssets = ({ handlePrev, handleNext }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [alignment, setAlignment] = useState("enterManually");
  const [files, setFiles] = useState([]);
  const [rows, setRows] = useState([]);
  // const [loading, setLoading] = useState(false);
  const [isDataModified, setIsDataModified] = useState(false);
  const [dataEntryAlignment, setDataEntryAlignment] = useState("enterManually");
  const [message, setMessage] = useState("");

  const { IndexProps } = useIndexLogic({toast});
  const { 
    assetTypes, 
    handleOpenAssetDlg, 
    handleCloseAssetDlg, 
    openAssetDlg, 
    selectedAsset, 
    handleSaveAsset, 
    handleOpenAssetTypeDlg,
    loading,
    setLoading,
    assets,
    handleEditAsset,
    handleDeleteAsset,
    openAssetTypeDlg,
    handleCloseAssetTypeDlg,
    selectedAssetType
  } = IndexProps;

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 2000000, // 2 MB in bytes
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    onDrop: (acceptedFiles) => {
      setLoading(true);
      setTimeout(() => {
        setFiles(acceptedFiles.map((file) => Object.assign(file)));
        setIsDataModified(true);
        setLoading(false);
      }, 1000);
    },
    onDropRejected: () => {
      toast.error("You can only upload 1 file with a maximum size of 2 MB.", {
        duration: 2000,
          style: {
      fontFamily: '"DM Sans", sans-serif',
      fontSize: '15px',
    },
    });
      // setMessage("You can only upload 1 file with a maximum size of 2 MB.");
      setLoading(false);
    },
  });

    // parse & rename keys (if you need to)
    const handleAlignment = (event, newAlignment) => {
      if (newAlignment != null) {
        setAlignment(newAlignment);
        setDataEntryAlignment(newAlignment);
      }
    };

    const handleNextPage = () => {
        handleNext();
    };
  
    const handlePrevious = () => {
        handlePrev();

    };

    
  const handleFileParse = async () => {
    const file = files[0];
    if (!file) {
      setMessage("Please select file to perform this action.");
      return;
    }

    let parsedData = [];

    const renameKeys = (data) => {
      return data.map((row) => {
        const newRow = {};
        const keyGroups = {};

        for (let key in row) {
          const match = key.match(/(.+)_([0-9]+)/);
          if (match) {
            const baseKey = match[1];
            const number = parseInt(match[2]);

            if (!keyGroups[baseKey]) {
              keyGroups[baseKey] = [];
            }
            keyGroups[baseKey].push(number);
          } else {
            keyGroups[key] = [0];
          }
        }

        for (let key in row) {
          const match = key.match(/(.+)_([0-9]+)/);
          if (match) {
            const baseKey = match[1];
            const number = parseInt(match[2]);

            const numbers = keyGroups[baseKey].sort((a, b) => a - b);
            const isValidSequence = numbers.every(
              (num, index) => num === index
            );

            if (isValidSequence) {
              newRow[`${baseKey}_${number + 1}`] = row[key];
            } else {
              newRow[key] = row[key];
            }
          } else {
            newRow[key] = row[key];
          }
        }
        return newRow;
      });
    };

    if (file.type === "text/csv") {
      Papa.parse(file, {
        skipEmptyLines: true,
        header: true,
        complete: (result) => {
          parsedData = renameKeys(result.data);
          navigateToNextPage(parsedData);
          setLoading(false);
        },
      });
    } else if (
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Parse with header row and default value for empty cells
        let dataRows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        // Extract headers and map rows
        const headers = dataRows[0];
        const rows = dataRows.slice(1).map((row) =>
          headers.reduce((acc, key, i) => {
            acc[key] = row[i] || "";
            return acc;
          }, {})
        );

        parsedData = renameKeys(rows);
        navigateToNextPage(parsedData);
        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const navigateToNextPage = (data) => {
    navigate("/onboarding/upload-asset", { state: { data } });
  };

  // Render file preview
  const renderFilePreview = (file) => {
    if(file.type === "text/csv"){
      return <img src={CSV_Placeholder} />
    }else{
      return <img src={XLSX_Placeholder} />
    }
  };

  // Handle file removal
  const handleRemoveFile = (file) => {
    const filteredFiles = files.filter((i) => i.name !== file.name);
    setFiles(filteredFiles);
  };

  const fileList = files.map((file) => (
   <Box
      key={file.name}
      sx={{
        border: '1px solid #E1E4EA',
        borderRadius: 1,
        px: 6,
        py: 4,
        mx: 'auto',
        minWidth: {xs: 250, sm: 300},
        maxWidth: 'max-content',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mr: 6,
          }}
        >
          <Box>
            {renderFilePreview(file)}
          </Box>
          <Box>
            <Tooltip title={file.name}>
              <Typography 
                variant='body1'
                sx={{
                  maxWidth: '300px',
                  whiteSpace: {xs: 'wrap', sm: 'nowrap'},
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block'
                }}
              >
                {file.name}
              </Typography>
            </Tooltip>
            <Typography variant="body1" color="text.secondary">
              {Math.round(file.size / 100) / 10 > 1000
                ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
                : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Remove">
          <IconButton onClick={() => handleRemoveFile(file)}
             color="error">
            <Icon icon="tabler:trash" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  ));

  const columns = [
    {
      flex: 0.1,
      minWidth: 140,
      field: "assetName",
      headerName: "Asset Name",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.assetName}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "assetType",
      headerName: "Asset Type",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.assetType}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "serialVINNumber",
      headerName: "Serial/VIN Number",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.serialVINNumber}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
          <IconButton size="small" onClick={ () => handleEditAsset(row)}>
            <Icon icon="tabler:pencil" />
          </IconButton>
          <IconButton size="small" color='error' onClick={() => handleDeleteAsset(row.assetID)}>
            <Icon icon="tabler:trash" />
          </IconButton>
        </Box>
      )
    }
];

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: { xs: 0, sm: 2 },
           pt: { xs: 3, sm: "30px", md: "30px", lg: 2, xl: 2 },
          py: 3,
        }}
      >
        {message && (
          <Box mb={2} textAlign="center">
            <Typography
              variant="h6"
              color={message.includes("successfully") ? "green" : "red"}
            >
              {message}
            </Typography>
          </Box>
        )}
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                maxWidth: { xs: "100%", lg: "60%" },
                mx: "auto",
                gap: { xs: 3, sm: 0 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                  textAlign: { xs: "center", sm: "left" },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "white",
                    border: "1px solid #e0e0e0",
                    color: "text.primary",
                    height: { xs: 40, sm: 50 }, 
                    width: { xs: 40, sm: 50 },
                    mb: { xs: 1, sm: 0 }, 
                  }}
                >
                  <Icon icon="material-symbols-light:local-shipping" />
                </Avatar>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{ fontSize: { xs: "1.24rem", sm: "1.24rem" } }}
                  >
                    Add Assets
                  </Typography>
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.875rem", sm: "0.9rem" }, 
                    }}
                  >
                    Keep track of your long term physical assets, like
                    equipment, tools, and vehicles.
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  width: { xs: "100%", sm: "auto" }, 
                  display: "flex",
                  justifyContent: { xs: "center", sm: "flex-start" }, 
                  ml: { xs: 0, sm: 9 }, 
                  mt: { xs: 2, sm: 0 }, 
                }}
              >
                <Button
                  variant="contained"
                  color="main"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAssetDlg}
                  sx={{
                    px: 3,
                    width: { xs: "100%", sm: "auto" }, // Full width on mobile
                  }}
                >
                  New Asset
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TabContext value={alignment}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TabList onChange={handleAlignment}>
                  <Tab value="enterManually" label="Add Assets" />
                  <Tab value="uploadFile" label="Import Assets" />
                </TabList>
              </Box>
              <TabPanel value="enterManually" sx={{ px: 0 }}>
                {assets.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mx: "auto",
                      border: "1px solid #EAEAEA",
                      borderRadius: 1,
                      maxWidth: { xs: "100%", lg: "60%" },
                      py: 4,
                      height: 220,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={AddEmployeeDoodle}
                        alt="Empty State"
                        style={{ width: "100px", height: "auto" }}
                      />
                      <Typography variant="h6">No Assets added yet</Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <DataGridPro
                      columns={columns}
                      rows={assets}
                      sx={{
                        "& .MuiDataGrid-columnHeaderTitle": {
                          textTransform: "none",
                        },
                        minHeight: 250,
                        maxHeight: 350,
                        maxWidth: { xs: "100%", lg: "60%" },
                      }}
                      processRowUpdate={(newRow, oldRow) => {
                        const updatedRows = rows.map((row) =>
                          row.id === oldRow.id ? newRow : row
                        );
                        setRows(updatedRows);
                        return newRow;
                      }}
                      experimentalFeatures={{ newEditingApi: true }}
                      rowSelection={false}
                    />
                  </Box>
                )}
              </TabPanel>
              <TabPanel value="uploadFile" sx={{ px: 0 }}>
                <Grid item xs={12} sx={{ mb: 4 }}>
                  {/* <Card sx={{ cursor: "pointer" }}>
                    <CardContent sx={{ p: 10, flexGrow: 1, width: "100%" }}> */}
                 <Box
                      sx={{
                        border: '1px dashed #CACFD8',
                        borderRadius: 1,
                        py: 6,
                        px:{ xs: 2, sm: 6 },
                        maxWidth: {xs: '100%', lg: '60%'},
                        mx: 'auto',
                        cursor: 'pointer',
                      }}
                    >
                    <Fragment>
                      <div {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <Box
                          sx={{
                            display: "flex",
                            textAlign: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Box
                            textAlign="center"
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "100%", // Ensure it takes full height of parent
                              gap: 2, // Adds spacing between items (optional)
                            }}
                          >
                            <Icon
                              icon="tabler:cloud-upload"
                              fontSize="2.5rem"
                            />
                          </Box>
                          <Typography variant="h6">
                            Import multiple Assets by uploading an Excel
                            Spreadsheet or CSV file
                          </Typography>
                          <Typography variant="body2">
                            Click here to browse the files or drag your file
                            here
                          </Typography>
                        </Box>
                      </div>
                    </Fragment>
                  </Box>
                  <Box>
                    {files.length ? (
                      <>
                        <List>{fileList}</List>
                      </>
                    ) : null}
                  </Box>
                  {/* </CardContent>
                  </Card> */}
                </Grid>
              </TabPanel>
            </TabContext>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: { xs: "100%", lg: "60%"},
                }}
              >
                <Button
                  variant="text"
                  color="secondary"
                  onClick={handlePrevious}
                  sx={{ "& svg": { mr: 2 } }}
                >
                  {/* <Icon fontSize="1.125rem" icon="tabler:arrow-left" /> */}
                  Previous
                </Button>
                <Box>
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={handleNextPage}
                    size="small"
                    sx={{ mr: 2 }}
                    disabled={loading}
                  >
                    Skip for now
                  </Button>
                  <Button
                    variant="contained"
                    color="main"
                    disabled={loading}
                    // onClick={dataEntryAlignment === 'enterManually' ? handleSaveEmployees : handleFileParse}
                    onClick={
                      dataEntryAlignment === "uploadFile"
                        ? handleFileParse
                        : handleNextPage
                    }
                  >
                    {dataEntryAlignment === "enterManually" ? "Next" : "Import"}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <AddAssetDialog
        open={openAssetDlg}
        handleClose={handleCloseAssetDlg}
        handleSave={handleSaveAsset}
        setLoading={setLoading}
        selectedAsset={selectedAsset}
        onOpenAssetType={handleOpenAssetTypeDlg}
        assetTypes={assetTypes}
      />

      <AddAssetTypeDialog
        open={openAssetTypeDlg}
        handleClose={handleCloseAssetTypeDlg}
        setLoading={setLoading}
        selectedAssetType={selectedAssetType}
      />
      {/*<ToastContainer />*/}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default StepAddAssets;